// assets/stamp-design-carrier.js
// Receives the finished design handed off from the stamp studio and injects
// it into THIS product's real add-to-cart form as hidden line-item
// properties, so Shopify's normal, already-working Add to Cart takes over
// unchanged. Two sources, always both applied: (1) `?d=` — a URL-encoded
// JSON object of text-only properties, safe at any URL length, works even
// if localStorage was cleared or the link is opened in a different
// session; (2) `localStorage['stampDesign:'+sid]` — the fuller payload
// (logo, proof image, production SVG) written by the studio just before
// navigating here, read once and then removed.
//
// A plain visit to this product page with no ?sid/?d at all is a normal,
// fully purchasable product page — nothing in this file does anything.
import { Component } from '@theme/component';

const STORAGE_PREFIX = 'stampDesign:';

function escapeHtml(str) {
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

class StampDesignCarrier extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#hydrate();
  }

  #hydrate() {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('sid');
    const dParam = params.get('d');

    /** @type {Record<string, string>} */
    let textProps = {};
    if (dParam) {
      try {
        textProps = JSON.parse(dParam) || {};
      } catch (e) {
        textProps = {};
      }
    }

    /** @type {Record<string, string>} */
    let fullProps = { ...textProps };
    if (sid) {
      try {
        const raw = localStorage.getItem(STORAGE_PREFIX + sid);
        if (raw) {
          const entry = JSON.parse(raw);
          if (entry && entry.props) fullProps = { ...textProps, ...entry.props };
          localStorage.removeItem(STORAGE_PREFIX + sid);
        }
      } catch (e) {
        // Private browsing / corrupted entry — fall back to the text-only
        // properties already parsed above.
      }
    }

    if (Object.keys(fullProps).length === 0) return;

    this.#injectProps(fullProps);
    this.#renderSummary(fullProps);
    if (fullProps['Design mode'] === 'Custom uploaded design') {
      this.#showReupload(fullProps['Uploaded file']);
    }
  }

  #injectProps(props) {
    const container = this.refs.propsContainer;
    const formId = this.dataset.productFormId;
    if (!container) return;
    Object.entries(props).forEach(([key, value]) => {
      if (!value) return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${key}]`;
      input.value = value;
      if (formId) input.setAttribute('form', formId);
      container.appendChild(input);
    });
  }

  #renderSummary(props) {
    const { summaryBox, summaryImage, summaryList } = this.refs;
    if (!summaryBox) return;
    summaryBox.hidden = false;

    if (summaryImage && props['_Proof image']) {
      summaryImage.src = props['_Proof image'];
      summaryImage.hidden = false;
    }

    if (summaryList) {
      const rows = Object.entries(props).filter(([key]) => !key.startsWith('_'));
      summaryList.innerHTML = rows
        .map(([key, value]) => `<dt>${escapeHtml(key)}</dt><dd>${escapeHtml(value)}</dd>`)
        .join('');
    }
  }

  #showReupload(filename) {
    const { reuploadBox, reuploadHint } = this.refs;
    if (reuploadBox) reuploadBox.hidden = false;
    if (reuploadHint && filename) reuploadHint.textContent = `Re-attach your artwork (${filename})`;
  }
}

if (!customElements.get('stamp-design-carrier-component')) {
  customElements.define('stamp-design-carrier-component', StampDesignCarrier);
}
